import { ComponentType, ReactElement, ReactNode } from 'react'
import produce from 'immer'
import { reject } from 'lodash-es'
import { Vector3 } from 'three'
import type { Object3D } from 'three'
import create from 'zustand'
import createContext from 'zustand/context'
import { combine } from 'zustand/middleware'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { SceneContextMenuAction } from '@ui/scene/Context/types'
import { refusePaneChange } from '../blockActionStore'

export interface SceneElement<T extends object> {
  id: string
  properties: T
  component: ComponentType<T>
}

/** Mutually-exclusive drawer panel on the IFC Import page (`null` when none is active). */
export type IFCMode =
  | 'building'
  | 'settings'
  | 'add'
  | 'guided'
  | 'alignment'
  | 'filter'
  | 'storey'
  | 'type'
  | 'draw-roofs'
  | 'draw-slabs'
  | 'draw-walls'
  | 'draw-openings'
  | 'auto-connect'
  | null

/** Which model the scene selection acts on (mutually exclusive). */
export type SelectionMode = 'ifc-selection' | 'planar-model-selection'

/** Kind of plan element a `SelectedPlanElement` refers to. */
export type PlanElementType = 'table' | 'image-card' | 'comment' | 'element-label'

/**
 * The one plan element (table / image card / comment) that is currently selected. These selections
 * are mutually exclusive, so they share a single state instead of one `selectedId` per store —
 * selecting any of them implicitly deselects the others, and an id can never be read as the wrong
 * type.
 */
export interface SelectedPlanElement {
  type: PlanElementType
  id: string
}

export type SnappingFeaturesEnabled = {
  snapToAngle: boolean
  snapOrthogonal: boolean
  snapToCornersAndEdges: boolean
  snapToTapelineCenters: boolean
  snapToComments: boolean
}

type SmartSuggestionStoreItemBase = {
  id: string
  text: string
  callback: () => void
  onAbort?: () => void
  enabled: boolean
}

export type SmartSuggestionStoreItem =
  | (SmartSuggestionStoreItemBase & {
      type: 'html-suggestion'
    })
  | (SmartSuggestionStoreItemBase & {
      type: 'scene-point-suggestion'
      point: { x: number; y: number; z: number }
    })

interface Props {
  children: ReactNode
}

export type StructuralPlanningElement =
  | 'stiffening'
  | 'connectors'
  | 'lineLoads'
  | 'pointLoads'
  | 'areaLoads'
  | 'verticalTransmitters'
  | 'windLoadZones'
  | 'horizontalWindLoads'

interface ControlStoreInitialState {
  gridEnabled: boolean
  axesEnabled: boolean
  coordinateExplorerEnabled: boolean
  applyClipPlanes: boolean
  clipPlanesInteractive: boolean
  /** Picked world points while defining a clip plane by three clicks (see ClipController). */
  pendingClipPlanePoints: Vector3[]
  clipPlaneResetVersion: number
  componentDetailEnabled: boolean
  snappingFeaturesEnabled: SnappingFeaturesEnabled
  orthographicDrawMode: boolean
  selectionMessage: string | null
  selectionModeKey: string | null
  selectionAbortCallback: (() => void) | null
  marqueeSelectionListeners: Map<
    string,
    {
      listener: (ids: string[], objects: Object3D[]) => void
      enabled: boolean
    }
  >
  clickListeners: Map<
    string,
    {
      listener: ModelClickListener
      isExclusive: boolean
      enableOnMultiSelect: boolean
      enableOnIfcMode: boolean
    }
  >
  hoverListeners: ModelHoverListener[][]
  actionMode: ActionMode
  hiddenElementIds: Set<string>
  widgetPortalElement: HTMLDivElement | null
  storeyControlsPortalElement: HTMLDivElement | null
  snackbarAnchorElement: HTMLElement | null
  // Guids of the callers currently requesting the scene to be blocked. The scene is blocked while
  // any group is registered, so independent callers don't clobber each other (see `useBlockScene`).
  sceneBlockGroups: Set<string>
  isSceneBlocked: boolean

  isDrawingActive: boolean

  isDrawerExpanded: boolean

  isBottomDrawerExpanded: boolean

  // SNAPPING

  snapToCornersAndEdges: boolean
  snapToAngles: boolean
  snapAngle: number
  snapOrthogonal: boolean
  snapToTapelineCenters: boolean
  snapToComments: boolean

  /** When true, Wand verschieben also shortens/lengthens walls connected exactly on the moved wall. */
  intelligentWallEditEnabled: boolean

  sceneElements: SceneElement<object>[]

  sceneLoadingIndicators: {
    id: string
    description?: string
  }[]

  sceneContextMenuActions: SceneContextMenuAction[]

  // COLORING MODE
  problemViewActiveErrors: boolean
  problemViewActiveWarnings: boolean
  coloringMode: ColoringMode
  selectedAssembliesForColoring: Set<string>
  coloringAnchorEl: HTMLElement | null
  propertiesPopupSelectedElement: string | null

  /** Wall guid to highlight as copy-rips origin (multi-edit wall rips "Rippen kopieren") */
  copyRipsOriginWallGuid: string | null

  // CONTEXT MENU
  contextMenu: {
    point: Vector3 | null
    elementId: string | null
    actions: Record<string, unknown>
    userData: Record<string, unknown>
  } | null

  screenshotToolElement: HTMLElement | null

  ifcUploadToolElement: HTMLElement | null

  /** Guid of the IFC/GLB file currently displayed in the 3D viewer. */
  activeIfcFileGuid: string | null

  /**
   * Visibility by IFC element type (MGroup) for the GLB model.
   * Only hidden types are stored (false); missing keys mean visible (true).
   */
  glbTypeVisibility: Record<string, boolean>
  /**
   * Per-storey IFC/GLB element type visibility (storey -> type -> visible). Used by the IFC storey
   * controls to hide a type on a single storey. Only hidden combinations are stored (false); a
   * missing key means visible (true).
   */
  glbTypeVisibilityByStorey: Record<string, Record<string, boolean>>
  /**
   * Whole-storey IFC/GLB visibility: storeys mapped to `true` hide all their IFC elements
   * (regardless of type). Missing keys mean visible. Toggled by the IFC storey controls' eye icon.
   */
  glbHiddenStoreys: Record<string, boolean>
  /**
   * When true, IFC/GLB elements filtered out in the Filter step (metadata `filteredElements`) are
   * shown in the scene anyway. Off by default — filtered elements stay hidden.
   */
  showFilteredIfcElements: boolean

  floorplanState: FloorplanState

  showGltfModel: boolean
  applyIfcAlignmentConfig: boolean
  gltfModelTranslucent: boolean

  highlightedIfcElementGuids: string[]

  /**
   * Whether the guided IFC import's upload step is already satisfied (a file was chosen up front or
   * has been uploaded), so the wizard marks it done and skips it.
   */
  ifcGuidedUploadCompleted: boolean

  /** Which model the scene selection acts on — IFC/GLB model or the planar (structural) model. */
  selectionMode: SelectionMode

  /**
   * Restricts which planar-model element types can be selected while planar-model selection is
   * active (IFC import). `null` means all supported planar types are selectable.
   */
  planarSelectableTypes: ElementTypes[] | null

  /** DOM node the guided IFC toolbar is portaled into (registered by the IFC Import toolbar). */
  ifcGuidedToolbarElement: HTMLDivElement | null

  /** Collapsed state of the structural-planning / global-loads element list pane. */
  elementListCollapsed: boolean

  /** Collapsed state of the Results2 list pane (shown by default — it drives results navigation). */
  resultsListCollapsed: boolean

  /**
   * Collapsed state of the Results2 detail pane's collapsible sections (checks, supports, ...),
   * keyed by a stable per-section id. Persisted here (not local component state) so it survives the
   * detail pane remounting when the user switches between elements. Missing keys mean expanded.
   */
  resultsSectionCollapsed: Record<string, boolean>

  /**
   * Compact mode for the "Modell" column of the storey controls. When true, the secondary
   * element-type toggles (UZ/C/WS/FS/P and the DA-/DE-Fläche/Balken buttons) are hidden, leaving
   * only the primary DE-Pos/AW/IW/DA-Pos/Öffnungen toggles.
   */
  modelColumnCompact: boolean

  isMarqueeSelection: boolean
  rotationTarget: ImmutableVector3 | null

  hiddenElementIdGroups: Record<string, string[]>

  /**
   * Element guids whose tensile transmitters the global tensile transmitter mesh must not draw,
   * grouped by the registering component (e.g. a form that previews its unsaved graph instead)
   */
  hiddenTensileTransmitterElementIdGroups: Record<string, string[]>

  elementSelectionDisabled: boolean

  smartSuggestions: SmartSuggestionStoreItem[]

  /**
   * Structural-planning element/overlay visibility (wind loads, supports, connectors, load types).
   * Lives here (not in structuralPlanningStore) so the toggles persist across editor navigation.
   */
  structuralPlanningElementVisibility: Record<StructuralPlanningElement, boolean>

  /** Whether model element labels are shown. Split out of modelStore's `labelVisibilityModel` so it
   *  persists across navigation; the per-type label visibility stays in modelStore. */
  showLabels: boolean

  /** Whether floorplan element labels also show the cross section line, like the plan PDF export. */
  showLabelCrossSections: boolean
}

type SelectionModeConfig =
  | {
      message: string
      key?: 'default' | string
      onAbort?: () => void
    }
  | undefined

interface ControlStoreType extends ControlStoreInitialState {
  toggleGridEnabled: () => void
  toggleAxesEnabled: () => void
  setAxesEnabled: (axesEnabled: boolean) => void
  toggleComponentDetailEnabled: () => void
  toggleSelectionMode: (config?: SelectionModeConfig) => void
  setSnappingFeaturesEnabled: (snappingFeaturesEnabled: SnappingFeaturesEnabled) => void
  setOrthographicDrawMode: (enabled: boolean) => void
  setClickListener: (
    id: string,
    listener: ModelClickListener,
    isExclusive?: boolean,
    enableOnMultiSelect?: boolean,
    enableOnIfcMode?: boolean,
  ) => void
  removeClickListener: (id: string) => void
  setHoverListeners: (listeners: ModelHoverListener[]) => void
  removeHoverListeners: (listeners: ModelHoverListener[]) => void
  setActionMode: (mode: ActionMode | null) => void
  addHiddenElementIds: (ids: string[]) => void
  removeHiddenElementIds: (ids: string[]) => void
  setAllElementsVisible: () => void
  setWidgetPortalElement: (ref: HTMLDivElement) => void
  setStoreyControlsPortalElement: (ref: HTMLDivElement) => void
  registerSnackbarAnchor: (element: HTMLElement | null) => void
  addSceneBlockGroup: (guid: string) => void
  removeSceneBlockGroup: (guid: string) => void

  setIsDrawingActive: (active?: boolean) => void
  setIsDrawerExpanded: (isDrawerExpanded: boolean) => void
  setIsBottomDrawerExpanded: (isBottomDrawerExpanded: boolean) => void

  // SNAPPING

  setSnapToCornersAndEdges: (snapToCornersAndEdges?: boolean) => void
  setSnapToAngles: (setSnapToAngles?: boolean) => void
  setSnapAngle: (snapAngle: number) => void
  setSnapOrthogonal: (snapOrthogonal?: boolean) => void
  setSnapToTapelineCenters: (snapToTapelineCenters?: boolean) => void
  setSnapToComments: (snapToComments?: boolean) => void
  setIntelligentWallEditEnabled: (intelligentWallEditEnabled: boolean) => void
  setCoordinateExplorerEnabled: (coordinateExplorerEnabled: boolean) => void
  setApplyClipPlanes: (applyClipPlanes: boolean) => void
  setClipPlanesInteractive: (clipPlanesInteractive: boolean) => void
  setPendingClipPlanePoints: (points: Vector3[]) => void
  resetClipPlanes: () => void
  addSceneContextMenuAction: (action: SceneContextMenuAction) => void
  removeSceneContextMenuAction: (action: SceneContextMenuAction) => void
  /**
   * Always use this with useSceneElement / useSceneLineMarker / useSceneMarker ... hooks.
   * @param marker
   * @returns
   */
  addSceneElement: <T extends object>(marker: SceneElement<T>) => void
  removeSceneElement: (id: string) => void
  /** Replaces the properties of a registered scene element, keeping its id (no remount) */
  updateSceneElement: <T extends object>(id: string, properties: T) => void

  addSceneLoadingIndicator: (indicator: { id: string; description?: string }) => void
  removeSceneLoadingIndicator: (id: string) => void

  // COLORING MODE
  setProblemViewActiveErrors: (problemViewActiveErrors: boolean) => void
  setProblemViewActiveWarnings: (problemViewActiveWarnings: boolean) => void
  setColoringMode: (coloringMode: ColoringMode) => void
  setSelectedAssembliesForColoring: (selectedAssembliesForColoring: Set<string>) => void
  setColoringAnchorEl: (coloringAnchorEl: HTMLElement | null) => void
  setPropertiesPopupSelectedElement: (selectedElement: string | null) => void
  setCopyRipsOriginWallGuid: (guid: string | null) => void

  // CONTEXT MENU
  setContextMenu: (
    contextMenu: {
      point: Vector3 | null
      elementId: string | null
      actions: Record<string, unknown>
      userData: Record<string, unknown>
    } | null,
  ) => void

  setScreenshotToolElement: (screenshotToolElement: HTMLElement | null) => void

  setIfcUploadToolElement: (ifcUploadToolElement: HTMLElement | null) => void

  setActiveIfcFileGuid: (activeIfcFileGuid: string | null) => void

  setGlbTypeVisibility: (type: string, visible: boolean) => void
  setGlbTypeVisibilityForAll: (visible: boolean, types?: string[]) => void
  clearGlbTypeVisibility: () => void
  setGlbTypeVisibilityForStorey: (storey: string, type: string, visible: boolean) => void
  clearGlbTypeVisibilityByStorey: () => void
  setGlbStoreyHidden: (storey: string, hidden: boolean) => void
  setShowFilteredIfcElements: (showFilteredIfcElements: boolean) => void

  setFloorplanStorey: (storey: string | null) => void

  setShowGltfModel: (showGltfModel: boolean) => void
  setApplyIfcAlignmentConfig: (applyIfcAlignmentConfig: boolean) => void
  setGltfModelTranslucent: (gltfModelTranslucent: boolean) => void

  setIfcGuidedUploadCompleted: (ifcGuidedUploadCompleted: boolean) => void
  setSelectionMode: (selectionMode: SelectionMode) => void
  setPlanarSelectableTypes: (planarSelectableTypes: ElementTypes[] | null) => void
  setIfcGuidedToolbarElement: (ref: HTMLDivElement | null) => void
  setElementListCollapsed: (elementListCollapsed: boolean) => void
  setResultsListCollapsed: (resultsListCollapsed: boolean) => void
  setResultsSectionCollapsed: (sectionId: string, collapsed: boolean) => void
  setModelColumnCompact: (modelColumnCompact: boolean) => void
  setIsMarqueeSelection: (isMarqueeSelection: boolean) => void
  addMarqueeSelectionListener: (
    id: string,
    listener: (ids: string[], objects: Object3D[]) => void,
    enabled: boolean,
  ) => void
  removeMarqueeSelectionListener: (id: string) => void

  setRotationTarget: (rotationTarget: ImmutableVector3 | null) => void
  setHighlightedIfcElementGuids: (guids: string[]) => void

  addHiddenElementIdGroup: (idGroup: string, ids: string[]) => void
  removeHiddenElementIdGroup: (idGroup: string) => void

  addHiddenTensileTransmitterElementIdGroup: (idGroup: string, ids: string[]) => void
  removeHiddenTensileTransmitterElementIdGroup: (idGroup: string) => void

  setElementSelectionDisabled: (elementSelectionDisabled: boolean) => void

  addSmartSuggestion: (suggestion: SmartSuggestionStoreItem) => void
  removeSmartSuggestion: (id: string) => void
  setSmartSuggestionEnabled: (id: string, enabled: boolean) => void

  changeStructuralPlanningElementVisibility: (
    element: StructuralPlanningElement,
    visibility: boolean,
  ) => void
  setShowLabels: (show: boolean) => void
  setShowLabelCrossSections: (show: boolean) => void
}

const initialState: ControlStoreInitialState = {
  actionMode: 'select',
  orthographicDrawMode: false,
  snappingFeaturesEnabled: {
    snapOrthogonal: true,
    snapToAngle: true,
    snapToCornersAndEdges: true,
    snapToTapelineCenters: true,
    snapToComments: true,
  },
  snapToCornersAndEdges: true,
  snapToAngles: false,
  snapAngle: 90,
  snapOrthogonal: false,
  snapToTapelineCenters: true,
  snapToComments: true,
  intelligentWallEditEnabled: true,

  selectionMessage: null,
  selectionModeKey: null,
  selectionAbortCallback: null,
  marqueeSelectionListeners: new Map(),
  clickListeners: new Map(),
  hoverListeners: [],
  gridEnabled: false,
  axesEnabled: false,
  hiddenElementIds: new Set(),
  widgetPortalElement: null,
  storeyControlsPortalElement: null,
  snackbarAnchorElement: null,
  sceneBlockGroups: new Set(),
  isSceneBlocked: false,

  componentDetailEnabled: false,

  isDrawingActive: false,
  isDrawerExpanded: true,

  isBottomDrawerExpanded: false,

  coordinateExplorerEnabled: false,
  applyClipPlanes: true,
  clipPlanesInteractive: true,
  pendingClipPlanePoints: [],
  clipPlaneResetVersion: 0,

  sceneContextMenuActions: [],
  sceneElements: [],

  sceneLoadingIndicators: [],

  // COLORING MODE
  problemViewActiveErrors: true,
  problemViewActiveWarnings: true,
  coloringMode: 'problemView',
  selectedAssembliesForColoring: new Set(),
  coloringAnchorEl: null,
  propertiesPopupSelectedElement: null,
  copyRipsOriginWallGuid: null,

  // CONTEXT MENU
  contextMenu: null,

  screenshotToolElement: null,

  ifcUploadToolElement: null,

  activeIfcFileGuid: null,

  glbTypeVisibility: {},
  glbTypeVisibilityByStorey: {},
  glbHiddenStoreys: {},
  showFilteredIfcElements: false,

  floorplanState: {
    storey: null,
  },

  showGltfModel: false,
  applyIfcAlignmentConfig: true,
  gltfModelTranslucent: false,

  ifcGuidedUploadCompleted: false,
  selectionMode: 'ifc-selection',
  planarSelectableTypes: null,
  ifcGuidedToolbarElement: null,
  elementListCollapsed: true,
  resultsListCollapsed: false,
  resultsSectionCollapsed: {},
  modelColumnCompact: false,
  isMarqueeSelection: false,

  rotationTarget: null,
  highlightedIfcElementGuids: [],
  hiddenElementIdGroups: {},
  hiddenTensileTransmitterElementIdGroups: {},
  elementSelectionDisabled: false,
  smartSuggestions: [],

  structuralPlanningElementVisibility: {
    stiffening: true,
    connectors: true,
    lineLoads: false,
    pointLoads: true,
    areaLoads: false,
    verticalTransmitters: true,
    windLoadZones: false,
    horizontalWindLoads: false,
  },
  showLabels: false,
  showLabelCrossSections: false,
}

const createStore = () =>
  create(
    combine(
      {
        ...initialState,
      },
      set => ({
        registerSnackbarAnchor: (element: HTMLElement | null) =>
          set(
            produce(state => {
              state.snackbarAnchorElement = element
            }),
          ),

        changeStructuralPlanningElementVisibility: (
          element: StructuralPlanningElement,
          visibility: boolean,
        ) =>
          set(
            produce(state => {
              state.structuralPlanningElementVisibility[element] = visibility
            }),
          ),

        setShowLabels: (show: boolean) =>
          set(
            produce(state => {
              state.showLabels = show
            }),
          ),

        setShowLabelCrossSections: (show: boolean) =>
          set(
            produce(state => {
              state.showLabelCrossSections = show
            }),
          ),

        addSceneBlockGroup: (guid: string) =>
          set(
            produce(state => {
              state.sceneBlockGroups.add(guid)
              state.isSceneBlocked = state.sceneBlockGroups.size > 0
            }),
          ),

        removeSceneBlockGroup: (guid: string) =>
          set(
            produce(state => {
              state.sceneBlockGroups.delete(guid)
              state.isSceneBlocked = state.sceneBlockGroups.size > 0
            }),
          ),

        removeHiddenElementIds: (ids: string[]) =>
          set(
            produce(state => {
              ids.forEach(id => state.hiddenElementIds.delete(id))
            }),
          ),

        addHiddenElementIds: (ids: string[]) =>
          set(
            produce(state => {
              state.hiddenElementIds = new Set([...state.hiddenElementIds, ...ids])
            }),
          ),

        setAllElementsVisible: () =>
          set(
            produce(state => {
              state.hiddenElementIds = new Set()
            }),
          ),

        setActionMode: (actionMode: ActionMode | null) =>
          set(() => ({ actionMode: actionMode || 'select' })),
        toggleGridEnabled: () => set(({ gridEnabled }) => ({ gridEnabled: !gridEnabled })),

        toggleAxesEnabled: () => set(({ axesEnabled }) => ({ axesEnabled: !axesEnabled })),

        setAxesEnabled: (axesEnabled: boolean) => set(() => ({ axesEnabled })),

        toggleComponentDetailEnabled: () =>
          set(({ componentDetailEnabled }) => ({
            componentDetailEnabled: !componentDetailEnabled,
          })),

        toggleSelectionMode: (config?: SelectionModeConfig) =>
          set(
            produce(state => {
              state.selectionMessage = config?.message || null
              state.selectionModeKey = config ? config.key || 'default' : null
              state.selectionAbortCallback = config?.onAbort || null
            }),
          ),

        setClickListener: (
          id: string,
          listener: ModelClickListener,
          isExclusive = false,
          enableOnMultiSelect = false,
          enableOnIfcMode = false,
        ) =>
          set(
            produce(state => {
              state.clickListeners.set(id, {
                listener,
                isExclusive,
                enableOnMultiSelect,
                enableOnIfcMode,
              })
            }),
          ),

        setOrthographicDrawMode: (enabled: boolean) =>
          set(
            produce(state => {
              state.orthographicDrawMode = enabled
            }),
          ),

        setIsDrawerExpanded: (isDrawerExpanded: boolean) => set({ isDrawerExpanded }),

        setIsBottomDrawerExpanded: (isBottomDrawerExpanded: boolean) =>
          set({ isBottomDrawerExpanded }),

        removeClickListener: (id: string) =>
          set(
            produce(state => {
              state.clickListeners.delete(id)
            }),
          ),

        setHoverListeners: (listeners: ModelHoverListener[]) =>
          set(
            produce(state => {
              state.hoverListeners.push(listeners)
            }),
          ),

        removeHoverListeners: (listeners: ModelHoverListener[]) =>
          set(
            produce(state => {
              state.hoverListeners.splice(state.hoverListeners.indexOf(listeners), 1)
            }),
          ),

        setWidgetPortalElement: (ref: HTMLDivElement) =>
          set(
            produce(state => {
              state.widgetPortalElement = ref
            }),
          ),

        setStoreyControlsPortalElement: (ref: HTMLDivElement) =>
          set(
            produce(state => {
              state.storeyControlsPortalElement = ref
            }),
          ),

        setIsDrawingActive: (isDrawingActive?: boolean) => {
          set(
            produce(state => {
              state.isDrawingActive = isDrawingActive
            }),
          )
        },

        // SNAPPING

        setSnappingFeaturesEnabled: (snappingFeaturesEnabled: SnappingFeaturesEnabled) =>
          set({ snappingFeaturesEnabled }),

        setSnapToCornersAndEdges: (snapToCornersAndEdges?: boolean) => {
          set(
            produce(state => {
              state.snapToCornersAndEdges = snapToCornersAndEdges
            }),
          )
        },

        setSnapToAngles: (setSnapToAngles?: boolean) => {
          set(
            produce(state => {
              state.snapToAngles = setSnapToAngles
            }),
          )
        },

        setSnapAngle: (snapAngle: number) => {
          set(
            produce(state => {
              state.snapAngle = snapAngle
            }),
          )
        },

        setSnapOrthogonal: (snapOrthogonal?: boolean) => {
          set(
            produce(state => {
              state.snapOrthogonal = snapOrthogonal
            }),
          )
        },

        setSnapToTapelineCenters: (snapToTapelineCenters?: boolean) => {
          set(
            produce(state => {
              state.snapToTapelineCenters = snapToTapelineCenters
            }),
          )
        },

        setSnapToComments: (snapToComments?: boolean) => {
          set(
            produce(state => {
              state.snapToComments = snapToComments
            }),
          )
        },

        setIntelligentWallEditEnabled: (intelligentWallEditEnabled: boolean) => {
          set(
            produce(state => {
              state.intelligentWallEditEnabled = intelligentWallEditEnabled
            }),
          )
        },

        setCoordinateExplorerEnabled: (coordinateExplorerEnabled: boolean) => {
          set(
            produce(state => {
              state.coordinateExplorerEnabled = coordinateExplorerEnabled
            }),
          )
        },

        setApplyClipPlanes: (applyClipPlanes: boolean) => {
          set(
            produce(state => {
              state.applyClipPlanes = applyClipPlanes
            }),
          )
        },

        setClipPlanesInteractive: (clipPlanesInteractive: boolean) => {
          set(
            produce(state => {
              state.clipPlanesInteractive = clipPlanesInteractive
            }),
          )
        },

        setPendingClipPlanePoints: (pendingClipPlanePoints: Vector3[]) =>
          set({ pendingClipPlanePoints }),

        resetClipPlanes: () =>
          set(
            produce(state => {
              state.clipPlaneResetVersion += 1
            }),
          ),

        addSceneContextMenuAction: (action: SceneContextMenuAction) =>
          set(
            produce(state => {
              state.sceneContextMenuActions.push(action)
            }),
          ),

        removeSceneContextMenuAction: (action: SceneContextMenuAction) =>
          set(
            produce(state => {
              state.sceneContextMenuActions = reject(
                state.sceneContextMenuActions,
                a => a.id === action.id,
              )
            }),
          ),

        /**
         * Always use this with useSceneElement / useSceneLineMarker / useSceneMarker ... hooks.
         * @param marker
         * @returns
         */
        addSceneElement: <T extends object>(marker: SceneElement<T>) =>
          set(
            produce((state: ControlStoreInitialState) => {
              // @ts-ignore
              state.sceneElements.push(marker)
            }),
          ),

        removeSceneElement: (id: string) =>
          set(
            produce((state: ControlStoreInitialState) => {
              state.sceneElements = state.sceneElements.filter(m => m.id !== id)
            }),
          ),

        updateSceneElement: <T extends object>(id: string, properties: T) =>
          set(state => {
            const element = state.sceneElements.find(m => m.id === id)
            if (!element || element.properties === properties) return state

            return {
              sceneElements: state.sceneElements.map(m => (m.id === id ? { ...m, properties } : m)),
            }
          }),

        addSceneLoadingIndicator: (indicator: { id: string; description?: string }) =>
          set(
            produce((state: ControlStoreInitialState) => {
              state.sceneLoadingIndicators.push(indicator)
            }),
          ),

        removeSceneLoadingIndicator: (id: string) =>
          set(
            produce((state: ControlStoreInitialState) => {
              state.sceneLoadingIndicators = state.sceneLoadingIndicators.filter(i => i.id !== id)
            }),
          ),

        // COLORING MODE
        setProblemViewActiveErrors: (problemViewActiveErrors: boolean) =>
          set({ problemViewActiveErrors }),
        setProblemViewActiveWarnings: (problemViewActiveWarnings: boolean) =>
          set({ problemViewActiveWarnings }),
        setColoringMode: (coloringMode: ColoringMode) => set({ coloringMode }),
        setSelectedAssembliesForColoring: (selectedAssembliesForColoring: Set<string>) =>
          set({ selectedAssembliesForColoring }),
        setColoringAnchorEl: (coloringAnchorEl: HTMLElement | null) => set({ coloringAnchorEl }),
        setPropertiesPopupSelectedElement: (selectedElement: string | null) =>
          set({ propertiesPopupSelectedElement: selectedElement }),
        setCopyRipsOriginWallGuid: (copyRipsOriginWallGuid: string | null) =>
          set({ copyRipsOriginWallGuid }),

        // CONTEXT MENU
        setContextMenu: (
          contextMenu: {
            point: Vector3 | null
            elementId: string | null
            actions: Record<string, unknown>
            userData: Record<string, unknown>
          } | null,
        ) => set({ contextMenu }),

        setScreenshotToolElement: (screenshotToolElement: HTMLElement | null) =>
          set({ screenshotToolElement }),

        setIfcUploadToolElement: (ifcUploadToolElement: HTMLElement | null) =>
          set({ ifcUploadToolElement }),

        setActiveIfcFileGuid: (activeIfcFileGuid: string | null) => set({ activeIfcFileGuid }),

        setGlbTypeVisibility: (type: string, visible: boolean) =>
          set(
            produce(state => {
              if (visible) {
                const next = { ...state.glbTypeVisibility }
                delete next[type]
                state.glbTypeVisibility = next
              } else {
                state.glbTypeVisibility[type] = false
              }
            }),
          ),

        setGlbTypeVisibilityForAll: (visible: boolean, types?: string[]) =>
          set(
            produce(state => {
              if (visible) {
                state.glbTypeVisibility = {}
              } else if (types?.length) {
                const next: Record<string, boolean> = {}
                types.forEach(t => (next[t] = false))
                state.glbTypeVisibility = next
              }
            }),
          ),

        clearGlbTypeVisibility: () => set({ glbTypeVisibility: {} }),

        setGlbTypeVisibilityForStorey: (storey: string, type: string, visible: boolean) =>
          set(
            produce(state => {
              if (visible) {
                if (state.glbTypeVisibilityByStorey[storey]) {
                  delete state.glbTypeVisibilityByStorey[storey][type]
                  if (Object.keys(state.glbTypeVisibilityByStorey[storey]).length === 0)
                    delete state.glbTypeVisibilityByStorey[storey]
                }
              } else {
                if (!state.glbTypeVisibilityByStorey[storey])
                  state.glbTypeVisibilityByStorey[storey] = {}
                state.glbTypeVisibilityByStorey[storey][type] = false
              }
            }),
          ),

        clearGlbTypeVisibilityByStorey: () => set({ glbTypeVisibilityByStorey: {} }),

        setGlbStoreyHidden: (storey: string, hidden: boolean) =>
          set(
            produce(state => {
              if (hidden) {
                state.glbHiddenStoreys[storey] = true
              } else {
                delete state.glbHiddenStoreys[storey]
              }
            }),
          ),

        setShowFilteredIfcElements: (showFilteredIfcElements: boolean) =>
          set({ showFilteredIfcElements }),

        setFloorplanStorey: (storey: string | null) =>
          set(state => {
            const current = state.floorplanState.storey
            // Leaving 2D/3D or the storey can unmount a form with unsaved changes
            if (current !== storey && refusePaneChange()) return state

            return { floorplanState: { ...state.floorplanState, storey } }
          }),

        setShowGltfModel: (showGltfModel: boolean) => set({ showGltfModel }),
        setApplyIfcAlignmentConfig: (applyIfcAlignmentConfig: boolean) =>
          set({ applyIfcAlignmentConfig }),
        setGltfModelTranslucent: (gltfModelTranslucent: boolean) => set({ gltfModelTranslucent }),

        setIfcGuidedUploadCompleted: (ifcGuidedUploadCompleted: boolean) =>
          set({ ifcGuidedUploadCompleted }),
        setSelectionMode: (selectionMode: SelectionMode) => set({ selectionMode }),
        setPlanarSelectableTypes: (planarSelectableTypes: ElementTypes[] | null) =>
          set({ planarSelectableTypes }),
        setIfcGuidedToolbarElement: (ref: HTMLDivElement | null) =>
          set(
            produce(state => {
              state.ifcGuidedToolbarElement = ref
            }),
          ),

        setElementListCollapsed: (elementListCollapsed: boolean) => set({ elementListCollapsed }),

        setResultsListCollapsed: (resultsListCollapsed: boolean) => set({ resultsListCollapsed }),

        setResultsSectionCollapsed: (sectionId: string, collapsed: boolean) =>
          set(
            produce(state => {
              state.resultsSectionCollapsed[sectionId] = collapsed
            }),
          ),

        setModelColumnCompact: (modelColumnCompact: boolean) => set({ modelColumnCompact }),

        setIsMarqueeSelection: (isMarqueeSelection: boolean) => set({ isMarqueeSelection }),
        addMarqueeSelectionListener: (
          id: string,
          listener: (ids: string[], objects: Object3D[]) => void,
          enabled: boolean,
        ) =>
          set(
            produce(state => {
              state.marqueeSelectionListeners.set(id, { listener, enabled })
            }),
          ),
        removeMarqueeSelectionListener: (id: string) =>
          set(
            produce(state => {
              state.marqueeSelectionListeners.delete(id)
            }),
          ),
        setRotationTarget: (rotationTarget: ImmutableVector3 | null) => set({ rotationTarget }),
        setHighlightedIfcElementGuids: (highlightedIfcElementGuids: string[]) =>
          set({ highlightedIfcElementGuids }),
        addHiddenElementIdGroup: (idGroup: string, ids: string[]) =>
          set(
            produce(state => {
              state.hiddenElementIdGroups[idGroup] = ids
            }),
          ),
        removeHiddenElementIdGroup: (idGroup: string) =>
          set(
            produce(state => {
              delete state.hiddenElementIdGroups[idGroup]
            }),
          ),
        addHiddenTensileTransmitterElementIdGroup: (idGroup: string, ids: string[]) =>
          set(
            produce(state => {
              state.hiddenTensileTransmitterElementIdGroups[idGroup] = ids
            }),
          ),
        removeHiddenTensileTransmitterElementIdGroup: (idGroup: string) =>
          set(
            produce(state => {
              delete state.hiddenTensileTransmitterElementIdGroups[idGroup]
            }),
          ),

        setElementSelectionDisabled: (elementSelectionDisabled: boolean) =>
          set({ elementSelectionDisabled }),

        addSmartSuggestion: (suggestion: SmartSuggestionStoreItem) =>
          set(
            produce((state: ControlStoreInitialState) => {
              const index = state.smartSuggestions.findIndex(({ id }) => id === suggestion.id)
              if (index === -1) {
                state.smartSuggestions.push(suggestion)
              } else {
                state.smartSuggestions[index] = {
                  ...suggestion,
                  enabled: state.smartSuggestions[index].enabled,
                }
              }
            }),
          ),

        removeSmartSuggestion: (id: string) =>
          set(
            produce((state: ControlStoreInitialState) => {
              state.smartSuggestions = state.smartSuggestions.filter(
                suggestion => suggestion.id !== id,
              )
            }),
          ),

        setSmartSuggestionEnabled: (id: string, enabled: boolean) =>
          set(
            produce((state: ControlStoreInitialState) => {
              const suggestion = state.smartSuggestions.find(item => item.id === id)
              if (suggestion) suggestion.enabled = enabled
            }),
          ),
      }),
    ),
  )

const {
  Provider,
  useStore: useControlStore,
  useStoreApi: useControlStoreApi,
} = createContext<ControlStoreType>()

const ControlStoreProvider = ({ children }: Props): ReactElement => (
  <Provider createStore={createStore}>{children}</Provider>
)

export { ControlStoreProvider, useControlStore, useControlStoreApi }
